import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Plano, Inscricao } from '../../../models/planos/plano.model';
import { aplicarDescontoContador } from '../../../shared/utils/desconto-contador.util';

// Tipo auxiliar local para features do card
interface FeaturePlano {
  texto: string;
  icone: string;
  ativo: boolean;
}

@Component({
  selector: 'app-plano-card',
  templateUrl: './plano-card.component.html',
  styleUrls: ['./plano-card.component.scss'],
})
export class PlanoCardComponent {

  @Input() plano!: Plano;
  @Input() planoContratado: boolean = false;
  @Input() inscricaoAtiva: Inscricao | null = null;
  @Input() moeda: string = 'PYG';
  @Input() locale: string = 'es-PY';
  @Input() ehPlanoDestaque: boolean = false;
  @Input() cancelamentoPendente: boolean = false;
  // Verdadeiro quando o plano contratado esta no periodo de carencia
  // (vencido, mas ainda dentro dos 5 dias de gracia).
  @Input() emCarencia: boolean = false;
  // Desconto do contador vigente (incide sobre plano.valor). Ausencia = sem desconto.
  @Input() percentualContador?: number;
  @Input() nomeContador?: string;
  // Desconto de indicacao (R15) vigente (incide sobre plano.valor). Ausencia = sem
  // desconto. Precedencia MENOR que o contador (nunca exibir os dois juntos).
  @Input() percentualIndicacao?: number;
  @Input() codigoCupomIndicacao?: string;

  @Output() selecionarPlano = new EventEmitter<Plano>();
  @Output() cancelarAssinaturaEvt = new EventEmitter<void>();

  constructor(private translate: TranslateService) {}

  get ehGratuito(): boolean {
    return this.plano.valor === 0 || this.plano.planoStart === true;
  }

  get temDesconto(): boolean {
    return !!this.plano.valorOriginalSemDesconto
      && this.plano.valorOriginalSemDesconto > this.plano.valor;
  }

  /** So ha desconto de contador em plano PAGO (gratuito nunca — RN07). */
  get temDescontoContador(): boolean {
    return !this.ehGratuito && !!this.percentualContador && this.percentualContador > 0;
  }

  get valorComDescontoContador(): number {
    return aplicarDescontoContador(this.plano.valor, this.percentualContador ?? 0);
  }

  get labelSeloContador(): string {
    return this.translate.instant('planos.desconto_contador_selo');
  }

  /** Tooltip de origem (opcional) — "Desconto aplicado por {contador}". */
  get tooltipOrigemContador(): string {
    return this.translate.instant('planos.desconto_contador_origem', { contador: this.nomeContador ?? '' });
  }

  /** So ha desconto de indicacao em plano PAGO e quando NAO ha desconto de contador (precedencia). */
  get temDescontoIndicacao(): boolean {
    return !this.ehGratuito
      && !this.temDescontoContador
      && !!this.percentualIndicacao && this.percentualIndicacao > 0;
  }

  get valorComDescontoIndicacao(): number {
    return aplicarDescontoContador(this.plano.valor, this.percentualIndicacao ?? 0);
  }

  get labelSeloIndicacao(): string {
    return this.translate.instant('planos.desconto_indicacao_selo');
  }

  /** Tooltip de origem — "Desconto de indicacao: {percentual}% (cupom {cupom})". */
  get tooltipOrigemIndicacao(): string {
    return this.translate.instant('planos.desconto_indicacao_origem', {
      percentual: this.percentualIndicacao ?? 0,
      cupom: this.codigoCupomIndicacao ?? '',
    });
  }

  get podeClicar(): boolean {
    return this.plano.ativo === true
      && !this.planoContratado
      && !this.ehGratuito;
  }

  // true quando ha uma assinatura ativa que NAO e este card (o gratis nao e o vigente).
  // Usado para nao rotular o plano gratuito como "atual" quando ha um plano pago vigente.
  get existeOutroPlanoAtivo(): boolean {
    return this.inscricaoAtiva?.status === true
      && this.inscricaoAtiva?.plano?.id !== this.plano.id;
  }

  get podeCancelar(): boolean {
    return this.planoContratado
      && this.inscricaoAtiva?.tipoPagamento !== 'QR_VENDAS'
      && !this.cancelamentoPendente
      && !this.emCarencia; // na carencia mostramos Renovar no lugar de Cancelar
  }

  get exibirRenovar(): boolean {
    return this.planoContratado
      && (this.inscricaoAtiva?.tipoPagamento === 'QR_VENDAS' || this.emCarencia);
  }

  // Destaque: controlado pelo componente pai via @Input ehPlanoDestaque
  get ehDestaque(): boolean {
    return this.ehPlanoDestaque && !this.planoContratado;
  }

  get badgeTexto(): string {
    return this.translate.instant('planos.popular');
  }

  get badgeTipo(): string {
    return 'popular';
  }

  get labelPeriodo(): string {
    switch (this.plano.tipoPlano) {
      case 'MENSAL': return this.translate.instant('planos.por_mes');
      case 'TRIMESTRAL': return this.translate.instant('planos.por_trimestre');
      case 'ANUAL': return this.translate.instant('planos.por_ano');
      default: return '';
    }
  }

  get featuresPlano(): FeaturePlano[] {
    const features: FeaturePlano[] = [];

    // Feature 1: Limite de gasto
    if (this.plano.limiteDeGasto && this.plano.limiteDeGasto > 0) {
      features.push({
        texto: this.translate.instant('planos.limite_vendas') + ': ' +
          new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.moeda,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(this.plano.limiteDeGasto),
        icone: 'pi pi-check',
        ativo: true,
      });
    } else if (!this.ehGratuito) {
      features.push({
        texto: this.translate.instant('planos.ilimitado'),
        icone: 'pi pi-check',
        ativo: true,
      });
    }

    // Feature 2: Usuarios de acesso que o plano ja da direito, sem cobranca extra.
    // O fallback de 1 (o dono da empresa) so protege plano antigo sem o campo.
    const usuariosInclusos = this.plano.usuariosInclusos ?? 1;
    features.push({
      texto: usuariosInclusos === 1
        ? this.translate.instant('planos.usuario_incluso')
        : this.translate.instant('planos.usuarios_inclusos', { quantidade: usuariosInclusos }),
      icone: 'pi pi-check',
      ativo: true,
    });

    // Feature 3: Relatorios (disponivel em todos os planos, incluindo gratuito)
    features.push({
      texto: this.translate.instant('planos.relatorios'),
      icone: 'pi pi-check',
      ativo: true,
    });

    // Feature 4: Suporte (disponivel em planos pagos)
    features.push({
      texto: this.translate.instant('planos.suporte'),
      icone: this.ehGratuito ? 'pi pi-times' : 'pi pi-check',
      ativo: !this.ehGratuito,
    });

    return features;
  }

  onCardClick(): void {
    if (this.podeClicar) {
      this.selecionarPlano.emit(this.plano);
    }
  }

  // Renovacao reutiliza o mesmo fluxo de "Selecionar" (abrir dialog de pagamento).
  // Backend nao possui endpoint dedicado de renovacao — trata-se como nova assinatura.
  onRenovar(): void {
    this.selecionarPlano.emit(this.plano);
  }

  onCancelar(): void {
    this.cancelarAssinaturaEvt.emit();
  }

  formatarData(data: string | undefined): string {
    if (!data) return '';
    // Data "YYYY-MM-DD" deve ser formatada como LOCAL: new Date(string) a
    // interpreta como UTC e, em fusos negativos (ex.: Paraguai UTC-4), recua 1 dia.
    const iso = String(data).substring(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
    // Fallback: timestamp numerico em ms (ex: "1709049600000")
    let d = new Date(data);
    if (isNaN(d.getTime())) {
      const ts = Number(data);
      if (!isNaN(ts)) d = new Date(ts);
      if (isNaN(d.getTime())) return '';
    }
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const ano = d.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }
}
